import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useTonAddress, useTonConnectUI } from '@tonconnect/ui-react';
import {
  Users,
  Coins,
  Link2,
  Layers,
  ShieldCheck,
  Wallet,
  Ticket,
  Globe,
  Send,
  GraduationCap,
  ArrowRight,
} from 'lucide-react';
import type { PyramidaUiState } from '../hooks/usePyramidaState';
import { CONTRACT_PARAMS, IS_CONTRACT_CONFIGURED, TEAM_BOT_URL } from '../ton/config';
import {
  buildTelegramReferralLink,
  buildTeamTelegramReferralLink,
  buildSiteReferralLink,
  isTelegramMiniApp,
} from '../ton/referral';
import { ReferralLinkBlock } from './ReferralLinkBlock';
import { ShareStoryButton } from './ShareStoryButton';
import { ReferralCalculator } from './ReferralCalculator';
import { ReferralStatus } from './ReferralStatus';
import { ReferralLeaderboard } from './ReferralLeaderboard';
import { formatInt, formatTon } from '../lib/format';

interface ReferralsProps {
  state: PyramidaUiState;
  /** Switch to the Lottery tab (where the buy / registration CTA lives). */
  onGoToLottery?: () => void;
}

export function Referrals({ state, onGoToLottery }: ReferralsProps) {
  const { t } = useTranslation();
  const address = useTonAddress();
  const [tonConnectUI] = useTonConnectUI();
  const me = state.me;

  const hasKey = me?.registered && me.key != null;
  const key = hasKey ? me!.key! : 0;
  const tgLink = hasKey ? buildTelegramReferralLink(key) : null;
  const teamLink = hasKey ? buildTeamTelegramReferralLink(key) : '';
  const siteLink = hasKey ? buildSiteReferralLink(key) : '';
  const inMiniApp = isTelegramMiniApp();

  // Skeletons only make sense once a wallet is connected but the first
  // on-chain read for that account hasn't landed yet.
  const loadingMine = !!address && IS_CONTRACT_CONFIGURED && state.lastUpdated === null;
  const stat = (node: ReactNode) =>
    loadingMine ? <span className="skeleton skeleton--value" /> : node;

  // Inside the Telegram Mini App a plain anchor to t.me is blocked by the
  // webview; route it through Telegram's own opener instead.
  const openTeamBot = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const tg = window.Telegram?.WebApp;
    if (inMiniApp && typeof tg?.openTelegramLink === 'function') {
      e.preventDefault();
      tg.openTelegramLink(TEAM_BOT_URL);
    }
  };

  return (
    <section className="content-section">
      <header className="page-head">
        <h1 className="lottery-title">{t('referrals.title')}</h1>
        <span className="page-head__sub">
          {t('referrals.subtitle', {
            levels: CONTRACT_PARAMS.referralLevels,
            pct: CONTRACT_PARAMS.referralSharePercent,
          })}
        </span>
      </header>

      <div className="page-grid">
        <div className="page-grid__main">
          <div className="referral-link-card">
            <div className="card-head">
              <Link2 size={16} aria-hidden="true" /> {t('referrals.yourLink')}
            </div>
            {!address ? (
              <>
                <p className="hint hint--warn">{t('referrals.connectHint')}</p>
                <div className="referral-actions">
                  <button
                    type="button"
                    className="buy-ticket-btn buy-ticket-btn--sm"
                    onClick={() => tonConnectUI.openModal()}
                  >
                    <Wallet size={18} aria-hidden="true" /> {t('referrals.connectWallet')}
                  </button>
                </div>
              </>
            ) : !hasKey ? (
              <>
                <p className="hint hint--warn">{t('referrals.registerHint')}</p>
                <div className="referral-actions">
                  <button
                    type="button"
                    className="buy-ticket-btn buy-ticket-btn--sm"
                    onClick={() => onGoToLottery?.()}
                  >
                    <Ticket size={18} aria-hidden="true" /> {t('referrals.buyToGetLink')}
                  </button>
                </div>
              </>
            ) : inMiniApp ? (
              // Inside the Telegram Mini App: a single Telegram deep link, plus
              // a one-tap "share to Story" button.
              <>
                <ReferralLinkBlock
                  label={t('referrals.variantTelegramShort')}
                  icon={<Send size={15} aria-hidden="true" />}
                  hint={t('referrals.hintTelegram')}
                  link={tgLink ?? siteLink}
                  referrerKey={key}
                  action="share"
                />
                <ShareStoryButton referrerKey={key} />
              </>
            ) : (
              // On the web: let the user pick where to send referrals.
              <div className="ref-variants">
                <p className="hint">{t('referrals.pickWhere')}</p>
                {tgLink && (
                  <ReferralLinkBlock
                    label={t('referrals.variantTelegram')}
                    icon={<Send size={15} aria-hidden="true" />}
                    hint={t('referrals.hintTelegram')}
                    link={tgLink}
                    referrerKey={key}
                    action="download"
                  />
                )}
                <ReferralLinkBlock
                  label={t('referrals.variantSite')}
                  icon={<Globe size={15} aria-hidden="true" />}
                  hint={t('referrals.hintSite')}
                  link={siteLink}
                  referrerKey={key}
                  action="download"
                />
              </div>
            )}

            <ul className="trust-row">
              <li className="trust-chip">
                <Layers size={14} aria-hidden="true" />{' '}
                {t('referrals.chip', {
                  pct: CONTRACT_PARAMS.referralSharePercent,
                  levels: CONTRACT_PARAMS.referralLevels,
                })}
              </li>
              <li className="trust-chip">
                <ShieldCheck size={14} aria-hidden="true" /> {t('referrals.accrualOnchain')}
              </li>
            </ul>
          </div>

          {hasKey && (
            // Invite into the AI recruiter bot: the SAME numeric key attaches
            // on-chain (cross-bot intake), so earnings are identical — the only
            // difference is invitees get the AI instructor to coach them.
            <div className="referral-link-card">
              <div className="card-head">
                <GraduationCap size={16} aria-hidden="true" /> {t('referrals.inviteTeam.title')}
              </div>
              <p className="hint">{t('referrals.inviteTeam.body')}</p>
              <ReferralLinkBlock
                label={t('referrals.inviteTeam.variant')}
                icon={<GraduationCap size={15} aria-hidden="true" />}
                hint={t('referrals.inviteTeam.hint')}
                link={teamLink}
                referrerKey={key}
                action={inMiniApp ? 'share' : 'download'}
              />
              {inMiniApp && <ShareStoryButton referrerKey={key} bot="team" />}
              <p className="hint hint--ok">{t('referrals.inviteTeam.sameEarnings')}</p>
            </div>
          )}

          <a
            className="team-bot-card"
            href={TEAM_BOT_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={openTeamBot}
          >
            <div className="team-bot-card__icon" aria-hidden="true">
              <GraduationCap size={22} />
            </div>
            <div className="team-bot-card__body">
              <div className="team-bot-card__title">{t('referrals.teamBot.title')}</div>
              <p className="team-bot-card__text">{t('referrals.teamBot.body')}</p>
              <span className="team-bot-card__cta">
                {t('referrals.teamBot.cta')} <ArrowRight size={16} aria-hidden="true" />
              </span>
            </div>
          </a>

          <div className="token-info-card">
            <h3 className="card-head">
              <Layers size={18} aria-hidden="true" /> {t('referrals.howItWorks')}
            </h3>
            <ol className="how-list">
              <li>{t('referrals.step1')}</li>
              <li>{t('referrals.step2')}</li>
              <li>{t('referrals.step3')}</li>
              <li>{t('referrals.step4', { levels: CONTRACT_PARAMS.referralLevels })}</li>
            </ol>
          </div>

          <ReferralCalculator />
        </div>

        <div className="page-grid__side">
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-card__label">
                <Users size={13} aria-hidden="true" /> {t('referrals.yourReferrals')}
              </div>
              <div className="stat-card__value">{stat(me ? formatInt(me.referralsCount) : '—')}</div>
            </div>
            <div className="stat-card">
              <div className="stat-card__label">
                <Coins size={13} aria-hidden="true" /> {t('referrals.earned')}
              </div>
              <div className="stat-card__value">
                {stat(me ? `${formatTon(me.referralEarnings)} GRAM` : '— GRAM')}
              </div>
            </div>
          </div>

          {me?.key != null && (
            <div className="stat-card">
              <div className="stat-card__label">{t('referrals.yourKey')}</div>
              <div className="stat-card__value">#{me.key}</div>
            </div>
          )}

          {hasKey && (
            <ReferralStatus
              referralsCount={me!.referralsCount}
              earningsGram={me!.referralEarnings}
            />
          )}

          <ReferralLeaderboard myKey={hasKey ? key : null} />

          {!state.isLive && (
            <p className="hint">{t('referrals.demoHint')}</p>
          )}
        </div>
      </div>
    </section>
  );
}
